import "twin.macro";

import { useSelector } from "react-redux";

import { TableCell, TableRow } from "@mui/material";

import _ from "lodash";

import DebounceTextField from "@components/Utility/DebounceTextField";
import { usePurchaseOrder } from "@features/purchaseOrders/context/PurchaseOrderContext";
import { useUpdatePurchaseOrderMutation } from "@features/purchaseOrders/data";
import { formatMoneyString, limitTwoDecimals } from "@utility/utilityFunctions";
import { moneyAdornment } from "@utils/forms";
import useRoleIs from "@utils/useRoleIs";

const PurchaseOrderVariantTableFooter = ({
  totalRowsFirstCellLength,
  overview,
}: {
  totalRowsFirstCellLength: number;
  overview?: boolean;
}) => {
  const roleIs = useRoleIs();
  const { mutateAsync: updatePurchaseOrder } = useUpdatePurchaseOrderMutation();
  const {
    purchaseOrder,
    purchaseOrderVariants,
    poQuery: { isFetching },
  } = usePurchaseOrder();
  const { id } = purchaseOrder;

  const totalProductCost = _.sumBy(
    purchaseOrderVariants,
    (pov) => +pov.actualCost * pov.qty
  );
  const extendedTotal =
    totalProductCost +
    +purchaseOrder.totalFreightCost +
    +purchaseOrder.totalTaxes;

  const {
    organization: { includesTaxes },
  } = useSelector((state: any) => state.currentUser);

  const totalFreightEditable =
    (purchaseOrder.status !== "complete" || roleIs("super")) && !overview;

  const totalTaxEditable =
    purchaseOrder.status !== "complete" &&
    !overview &&
    purchaseOrder.type === "inventory";

  const showActionColPadding = purchaseOrder.status === "draft" && !overview;

  return (
    <>
      {purchaseOrder.type !== "fulfillment" && (
        <>
          <TableRow>
            <TableCell
              colSpan={
                totalFreightEditable
                  ? totalRowsFirstCellLength
                  : totalRowsFirstCellLength + 1
              }
            >
              <b>Total Freight Cost:</b>
            </TableCell>
            {totalFreightEditable && (
              <TableCell align="right" padding="checkbox">
                <DebounceTextField
                  value={limitTwoDecimals(purchaseOrder.totalFreightCost)}
                  handleSave={(value) => {
                    updatePurchaseOrder({ id, totalFreightCost: value });
                  }}
                  disabled={isFetching}
                  tw="m-0"
                  isNumberOnly
                  {...moneyAdornment}
                />
              </TableCell>
            )}
            <TableCell align="right">
              {formatMoneyString(purchaseOrder.totalFreightCost)}
            </TableCell>
            {showActionColPadding && <TableCell />}
          </TableRow>
          {includesTaxes && (
            <TableRow>
              <TableCell
                colSpan={
                  totalTaxEditable
                    ? totalRowsFirstCellLength
                    : totalRowsFirstCellLength + 1
                }
              >
                <b>Total Tax:</b>
              </TableCell>
              {totalTaxEditable && (
                <TableCell align="right" padding="checkbox">
                  <DebounceTextField
                    value={purchaseOrder.totalTaxCost ?? "0.00"}
                    handleSave={(value) => {
                      updatePurchaseOrder({ id, totalTaxCost: value });
                    }}
                    disabled={isFetching}
                    tw="m-0"
                    isNumberOnly
                    {...moneyAdornment}
                  />
                </TableCell>
              )}
              <TableCell align="right">
                {purchaseOrder.type === "inventory"
                  ? formatMoneyString(purchaseOrder.totalTaxCost)
                  : formatMoneyString(purchaseOrder.totalTaxes)}
              </TableCell>
              {showActionColPadding && <TableCell />}
            </TableRow>
          )}
        </>
      )}
      <TableRow>
        <TableCell colSpan={totalRowsFirstCellLength}>
          <b>Grand Total:</b>
        </TableCell>
        <TableCell align="right" colSpan={2} tw="text-lg!">
          {formatMoneyString(extendedTotal)}
        </TableCell>
        {showActionColPadding && <TableCell />}
      </TableRow>
    </>
  );
};

export default PurchaseOrderVariantTableFooter;
